import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listParties, listPartyGroups } from '@/app/services/partyService';
import { listTasks } from '@/app/services/taskService';
import { TaskStatus } from '@/app/types/task';
import QuickStatsCard from './components/QuickStatsCard';
import DashboardTaskPanels from '@/app/components/tasks/DashboardTaskPanels';
import ComplianceReturnsWidget from './components/ComplianceReturnsWidget';
import TeamPoolWidget from './components/TeamPoolWidget';
import TaskStatusCardsWidget from './components/TaskStatusCardsWidget';
import TaskCompletionCountersWidget from './components/TaskCompletionCountersWidget';
import TeamActivityFeedWidget from './components/TeamActivityFeedWidget';
import { Users, RefreshCw, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { useAuthStore } from '@/app/store/authStore';

const DashboardPage: React.FC = () => {
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);

    useEffect(() => {
        document.title = 'Dashboard - ETax';
        return () => {
            document.title = 'eTax App';
        };
    }, []);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    // Fetch "Client" party group to get its ID
    const { data: partyGroupsData } = useQuery({
        queryKey: ['partyGroups-dashboard'],
        queryFn: () => listPartyGroups({ limit: 100 }),
    });
    const clientGroupId = partyGroupsData?.items?.find(g => g.name === 'Client')?.id;

    // Fetch party (client) count - filtered by "Client" group
    const { data: partiesData, isLoading: partiesLoading } = useQuery({
        queryKey: ['parties-count', clientGroupId],
        queryFn: () => listParties({
            limit: 1,
            filters: JSON.stringify([{ field: 'party_group_id', op: 'eq', value: clientGroupId }]),
        }),
        enabled: !!clientGroupId,
    });
    const partyCount = partiesData?.total || 0;

    // Fetch open tasks count
    const { data: openTasksData, isLoading: openTasksLoading } = useQuery({
        queryKey: ['open-tasks-count'],
        queryFn: () => listTasks({ status: TaskStatus.OPEN, limit: 1 }),
    });
    const openTasksCount = openTasksData?.total || 0;

    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ['partyGroups-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['parties-count'] });
        queryClient.invalidateQueries({ queryKey: ['open-tasks-count'] });
        queryClient.invalidateQueries({ queryKey: ['compliance-dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['team-activity'] });
        queryClient.invalidateQueries({ queryKey: ['task-completion-stats'] });
    };

    const firstName = user?.full_name?.split(' ')[0] || user?.username || '';

    return (
        <div className="space-y-6 pb-8">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-xl font-semibold tracking-tight">
                        {getGreeting()}{firstName ? `, ${firstName}` : ''}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {format(new Date(), 'EEEE, MMMM d, yyyy')}
                    </p>
                </div>
                <Button
                    onClick={handleRefresh}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground gap-1.5 h-8"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Refresh
                </Button>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-5">
                <QuickStatsCard
                    title="Active Clients"
                    value={partyCount}
                    description="Registered in Client group"
                    icon={Users}
                    isLoading={partiesLoading}
                    accentColor="teal"
                    href="/admin/reporting/parties"
                />
                <QuickStatsCard
                    title="Open Tasks"
                    value={openTasksCount}
                    description="Awaiting completion"
                    icon={ListChecks}
                    isLoading={openTasksLoading}
                    accentColor="amber"
                />
            </div>

            {/* My / Team task panels — operationally actionable */}
            <DashboardTaskPanels />

            {/* Compliance Returns */}
            <ComplianceReturnsWidget />

            {/* Team Task Pool */}
            <TeamPoolWidget />

            {/* Task Overview */}
            <TaskStatusCardsWidget />

            {/* Team Section */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                <div className="lg:col-span-3">
                    <TeamActivityFeedWidget />
                </div>
                <div className="lg:col-span-2">
                    <TaskCompletionCountersWidget />
                </div>
            </div>

        </div>
    );
};

export default DashboardPage;
